import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { X, Loader2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MAX_VIRTUAL_MEMBERS_PER_GROUP = 10;

interface AddVirtualMemberDialogProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
}

export function AddVirtualMemberDialog({ open, onClose, groupId }: AddVirtualMemberDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      addMember.reset();
    }
  }, [open]);

  const addMember = useMutation({
    mutationFn: async (displayName: string) => {
      if (!user?.id) throw new Error('Not authenticated');

      // Check virtual member limit for this group
      const { count, error: countError } = await supabase
        .from('group_members')
        .select('id, users!inner(is_virtual)', { count: 'exact', head: true })
        .eq('group_id', groupId)
        .eq('_deleted', false)
        .eq('users.is_virtual', true);

      if (countError) throw countError;
      if ((count ?? 0) >= MAX_VIRTUAL_MEMBERS_PER_GROUP) {
        throw new Error(`Maximum ${MAX_VIRTUAL_MEMBERS_PER_GROUP} virtual members per group`);
      }

      // Create virtual user
      const userId = crypto.randomUUID();
      const now = new Date().toISOString();

      const { error: userError } = await supabase
        .from('users')
        .insert({
          id: userId,
          display_name: displayName.trim(),
          is_virtual: true,
          created_by_user_id: user.id,
          subscription_tier: 'FREE',
          created_at: now,
        });

      if (userError) throw userError;

      // Add as group member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          id: crypto.randomUUID(),
          group_id: groupId,
          user_id: userId,
          role: 'member',
          joined_at: now,
        });

      if (memberError) throw memberError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    addMember.mutate(name);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-surface-primary p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('groups:addMemberModal.addVirtualMember')}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-text-quaternary hover:text-text-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 flex gap-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 px-4 py-3 text-sm text-blue-700 dark:text-blue-400">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{t('web:addVirtualMemberDialog.addSomeoneWhoDoesnTHave')}</p>
            <p className="mt-1 text-blue-600/80 dark:text-blue-400/80">{t('web:addVirtualMemberDialog.youLlManageThisVirtualMember')}</p>
          </div>
        </div>

        {addMember.error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {addMember.error instanceof Error ? addMember.error.message : t('groups:virtualMemberModal.validation.addFailed')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="virtual-member-name" className="block text-sm font-medium text-text-secondary">
              {t('web:common.displayName')}
            </label>
            <input
              id="virtual-member-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              maxLength={50}
              autoFocus
              placeholder={t('web:addVirtualMemberDialog.eGLittleJohnny')}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border-secondary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={addMember.isPending || !name.trim()}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {addMember.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('groups:detail.action.addMember')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
